import React from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Heading,
  useToast,
  Button,
  useColorModeValue,
  VStack,
  Text,
  HStack,
  Spacer,
  Flex,
  IconButton,
  Skeleton,
} from "@chakra-ui/react";
import { CheckCircleIcon, WarningTwoIcon } from "@chakra-ui/icons";
import useAxios from "../../utils/useAxios";
import { Trans } from "@lingui/react/macro";
import { MdSettings } from "react-icons/md";
import { IrrigationOverviewType } from "../../types/irrigationInterface";
import { formatTimestamp, formatUTCTimeToLocal } from "../../utils/dateUtils";

function formatPeriod(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hours > 0) {
    let r = `${hours} hr`;
    if (minutes > 0) r += ` ${minutes} min`;
    return r;
  } else {
    let r = `${minutes} min`;
    if (secs > 0) r += ` ${secs.toFixed(0)} sec`;
    return r;
  }
}

const controllerModeLookup: { [key: number]: JSX.Element } = {
  0: <Trans>Manual</Trans>,
  1: <Trans>Schedule</Trans>,
  2: <Trans>Auto</Trans>,
};

interface Props {
  irrigation: IrrigationOverviewType;
  flowDataLoading?: boolean;
}

const IrrigationControllerTile: React.FC<Props> = ({ irrigation, flowDataLoading = false }) => {
  const labelColor = useColorModeValue("gray.700", "gray.300");
  const boxColor = useColorModeValue("white", "gray.900");
  const api = useAxios();
  const toast = useToast();
  const [localPendingState, setLocalPendingState] = React.useState<number | null>(null);

  const handleValve = async (state: boolean) => {
    const pendingState = state ? 1 : 0;
    setLocalPendingState(pendingState);
    try {
      const res = await api.post(`/agtech/irrigation/valve/${irrigation.id}/`, { state });
      toast({
        title: state ? <Trans>Opening Valve</Trans> : <Trans>Closing Valve</Trans>,
        description: res.data.message,
        status: "success",
        duration: 9000,
        isClosable: true,
      });
    } catch (err: any) {
      toast({
        title: state ? (
          <Trans>Error turning valve on</Trans>
        ) : (
          <Trans>Error turning valve off</Trans>
        ),
        description: err.message,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
      setLocalPendingState(null); // Clear pending state on error
    }
  };

  return (
    <Box p={4} shadow="md" borderRadius="lg" bg={boxColor} maxW={600}>
      <VStack spacing={4} w="100%">
        <HStack w="100%">
          <Link
            to={`/valve-view/${
              irrigation.controller_device_id || irrigation.controller_data?.device_id
            }`}
          >
            <Heading size="lg">
              {irrigation.controller_device_name || irrigation.controller_data?.device_name}
            </Heading>
          </Link>
          <Spacer />
          {(() => {
            // Use controller_last_seen if available (list view), otherwise fall back to controller_data (detail view)
            const lastSeenTime = irrigation.controller_last_seen
              ? new Date(irrigation.controller_last_seen).getTime()
              : irrigation.controller_data?.data?.timestamp
              ? new Date(irrigation.controller_data.data.timestamp).getTime()
              : null;

            const isOnline = lastSeenTime && Date.now() - lastSeenTime < 4 * 3600 * 1000;

            return isOnline ? (
              <>
                <CheckCircleIcon color="green.500" />
                <Text fontWeight={100}>
                  <Trans>Online</Trans>
                </Text>
              </>
            ) : (
              <>
                <WarningTwoIcon color="red.500" />
                <Text>
                  <Trans>Offline</Trans>
                </Text>
              </>
            );
          })()}

          <Spacer />

          <IconButton
            aria-label="Settings"
            icon={<MdSettings />}
            variant="outline"
            size="lg"
            as={Link}
            to={`/irrigation/${irrigation.id}`}
          >
            Settings
          </IconButton>
        </HStack>

        <Flex direction="row" w="100%">
          <Box p={5} flex="1">
            <Text color={labelColor}>
              <Trans>Mode:</Trans>
            </Text>
            <Heading size="md">{controllerModeLookup[irrigation.mode]}</Heading>

            {(irrigation.mode === 1 || irrigation.mode === 2) && (
              <>
                <Text color={labelColor}>
                  <Trans>Irrigation Time:</Trans>
                </Text>
                {!irrigation.start_time || !irrigation.end_time ? (
                  <Text>
                    <Trans>Always</Trans>
                  </Text>
                ) : (
                  <Text fontWeight={600}>
                    {formatUTCTimeToLocal(irrigation.start_time)} <Trans>to</Trans>{" "}
                    {formatUTCTimeToLocal(irrigation.end_time)}
                  </Text>
                )}

                <Text color={labelColor}>
                  <Trans>Interval:</Trans>
                </Text>
                <Text fontWeight={600}>
                  {irrigation.period ? formatPeriod(irrigation.period) : "-"}
                </Text>

                <Text color={labelColor}>
                  <Trans>Duration:</Trans>
                </Text>
                <Text fontWeight={600}>
                  {irrigation.period && irrigation.duration
                    ? formatPeriod(irrigation.duration)
                    : "-"}
                </Text>
              </>
            )}

            {irrigation.mode === 2 && (
              <Text>
                <Trans>Setpoint:</Trans> {irrigation.setpoint} %
              </Text>
            )}

            <>
              <Text color={labelColor}>
                <Trans>State:</Trans>
              </Text>
              {irrigation.current_valve_state || irrigation.state !== null ? (
                <>
                  <Text fontWeight={600}>
                    {irrigation.current_valve_state?.state ?? irrigation.state ? (
                      <Trans>Open</Trans>
                    ) : (
                      <Trans>Closed</Trans>
                    )}
                  </Text>
                  <Text color={labelColor}>
                    <Trans>Time set:</Trans>
                  </Text>
                  <Text fontWeight={600}>
                    {(irrigation.current_valve_state?.created_at ??
                      irrigation.valve_state_set_at) &&
                      formatTimestamp(
                        irrigation.current_valve_state?.created_at ?? irrigation.valve_state_set_at!
                      )}
                  </Text>
                </>
              ) : (
                <Text fontWeight={600}>
                  <Trans>Unknown</Trans>
                </Text>
              )}

              {(irrigation.pending_valve_command?.has_pending_command ||
                localPendingState !== null) && (
                <Text color="orange.500" fontWeight={600} mt={2}>
                  <Trans>
                    {(irrigation.pending_valve_command?.pending_state ?? localPendingState) === 1
                      ? "Opening valve..."
                      : "Closing valve..."}
                  </Trans>
                </Text>
              )}

              <Box mt={2}>
                <Button
                  size="sm"
                  mr={2}
                  onClick={() => handleValve(true)}
                  isDisabled={
                    irrigation.pending_valve_command?.has_pending_command ||
                    localPendingState !== null
                  }
                >
                  <Trans>Open</Trans>
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleValve(false)}
                  isDisabled={
                    irrigation.pending_valve_command?.has_pending_command ||
                    localPendingState !== null
                  }
                >
                  <Trans>Close</Trans>
                </Button>
              </Box>
            </>
          </Box>

          <VStack p={5} flex="1" align="start">
            {irrigation.sensor_data?.data?.soil_moisture && (
              <Link to={`/devices/${irrigation.sensor_data.device_id}`}>
                <Text color={labelColor}>
                  <Trans>Moisture:</Trans>
                </Text>
                <Heading size="md">
                  {irrigation.sensor_data.data.soil_moisture.toFixed(1)} %
                </Heading>
                <Text color={labelColor} fontWeight={100}>
                  {irrigation.sensor_data.data.timestamp
                    ? formatTimestamp(irrigation.sensor_data.data.timestamp)
                    : ""}
                </Text>
              </Link>
            )}

            {flowDataLoading ? (
              <>
                <Skeleton height="20px" width="120px" mb={2} />
                <Skeleton height="32px" width="100px" mb={1} />
                <Skeleton height="16px" width="140px" mb={3} />
                <Skeleton height="20px" width="100px" mb={2} />
                <Skeleton height="32px" width="80px" />
              </>
            ) : (
              irrigation.flow_measurement && (
                <>
                  <Text color={labelColor}>
                    <Trans>Current flow:</Trans>
                  </Text>
                  <Heading size="md">
                    {irrigation.flow_measurement.current_litres_per_minute.toFixed(1)} l/min
                  </Heading>
                  <Text color={labelColor} fontWeight={100}>
                    {irrigation.flow_measurement.last_updated_at
                      ? formatTimestamp(irrigation.flow_measurement.last_updated_at)
                      : ""}
                  </Text>

                  <Text color={labelColor}>
                    <Trans>Water meter:</Trans>
                  </Text>
                  <Heading size="md">
                    {irrigation.flow_measurement.total_water_volume_litres.toFixed(1)} l
                  </Heading>
                </>
              )
            )}
          </VStack>
        </Flex>
      </VStack>
    </Box>
  );
};

export default IrrigationControllerTile;
